import { createOrganization, createRoom } from '#~/api/agent/index.js';
import { config } from '#~/config/index.js';
import { StudioError } from '#~/error/index.js';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';

export function pluralize(name: string): string {
  const endsWithS = name.toLowerCase().endsWith('s');

  return endsWithS ? `${name}'` : `${name}'s`;
}

const isIris = config.vendor === 'iris';

function getRoomName(
  name: string,
  organizationName: T_Maybe<string>,
  isIrisVendor: boolean
): string {
  if (isIrisVendor) {
    return name;
  }

  const baseName = organizationName || (name ? pluralize(name) : null);
  return baseName ? `${baseName}'s Room` : 'My Room';
}

/**
 * Initializes the account by creating an organization and a room.
 *
 * @param token - The token to use for the request.
 * @param name - The name of the room.
 * @param organizationName - The actual organization name to use (optional).
 * @returns The id of the room.
 */
export const initAccount = async (
  token: string,
  name: string,
  organizationName?: T_Maybe<string>
): Promise<Room['id']> => {
  try {
    // Use the actual organization name if provided, otherwise fall back to the old pattern
    const orgName =
      organizationName || (name ? `${pluralize(name)} Organization` : 'My Organization');

    const orgId = await createOrganization(orgName, token);

    const roomName = getRoomName(name, organizationName, isIris);

    // createRoom now returns { id, alreadyExisted } (ENG-4209); this caller only
    // needs the id, so return `.id` — the Room['id'] contract is preserved.
    return (await createRoom(roomName, token, orgId)).id;
  } catch (error) {
    throw new StudioError('Unable to initialize account.', { cause: error });
  }
};
